import { _isNil, _notNil, getTeamIdFromStorage } from '@/littledash';
import type { TeamApiId, TeamTheme, ThemeCache } from '@/model/Team.model';
import { type Color } from '@/model/Common.model';

const STORAGE_KEY = 'team_themes';

const setVars = (primary: Color, onPrimary: Color): void => {
  document.body.style.setProperty('--iv-primary', primary);
  document.body.style.setProperty('--iv-on-primary', onPrimary);
};

const readCache = (): ThemeCache => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (_isNil(raw)) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    return parsed as ThemeCache;
  } catch {
    return {};
  }
};

const writeCache = (teamId: TeamApiId, theme: TeamTheme): void => {
  try {
    const cache = readCache();
    cache[teamId] = theme;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage full or unavailable
  }
};

const removeFromCache = (teamId: TeamApiId): void => {
  try {
    const cache = readCache();
    delete cache[teamId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage unavailable
  }
};

const applyFallback = (): void => {
  if (_notNil(AppConfig.theme)) {
    setVars(AppConfig.theme.primary, AppConfig.theme.on_primary);
  } else if (_notNil(AppConfig.config.brandingColor)) {
    setVars(AppConfig.config.brandingColor as Color, '#ffffff');
  } else {
    document.body.style.removeProperty('--iv-primary');
    document.body.style.removeProperty('--iv-on-primary');
  }
};

/**
 * Manages sidebar theme CSS variables and localStorage caching.
 * Stores a Record<TeamApiId, TeamTheme> so switching teams
 * can apply the correct cached theme immediately without a flash.
 */
export class TeamThemeService {
  static applyFromCache(): void {
    const teamId = getTeamIdFromStorage();
    if (_notNil(teamId)) {
      const cache = readCache();
      const cached = cache[teamId];
      if (_notNil(cached)) {
        setVars(cached.primary, cached.on_primary);
        return;
      }
    }
    applyFallback();
  }

  static apply(theme: TeamTheme): void {
    const teamId = getTeamIdFromStorage();
    if (_notNil(teamId)) {
      writeCache(teamId, theme);
    }
    setVars(theme.primary, theme.on_primary);
  }

  static preview(theme: TeamTheme): void {
    setVars(theme.primary, theme.on_primary);
  }

  static clear(): void {
    const teamId = getTeamIdFromStorage();
    if (_notNil(teamId)) {
      removeFromCache(teamId);
    }
    applyFallback();
  }
}
